/**
 * The app-wide product quick view (PR 10, plan I5) — "how is this product
 * doing" from any product row, without leaving the page.
 *
 * ProductQuickViewProvider (mounted once in App.tsx, around every tab) owns
 * the drawer; anything that shows a product calls `useProductQuickView()` and
 * `open(uid, marketplace)`. ProductThumb does it for every table that passes
 * it a `uid`.
 *
 * Outside the provider (tests, the public legal pages) the hook returns a
 * no-op with `available: false`, so a thumbnail there renders as a plain
 * image instead of a button that does nothing.
 */
import { createContext, useContext } from 'react'

export interface ProductQuickViewApi {
  /** Open the drawer on one product. `marketplace` is 'all' (default) or a
   *  hub marketplace name ('Amazon US', 'Walmart', …) — the scope its stock,
   *  money and status are read for. */
  open: (uid: string, marketplace?: string) => void
  close: () => void
  /** False outside the provider. */
  available: boolean
}

const NOOP: ProductQuickViewApi = { open: () => {}, close: () => {}, available: false }

export const ProductQuickViewContext = createContext<ProductQuickViewApi>(NOOP)

export function useProductQuickView(): ProductQuickViewApi {
  return useContext(ProductQuickViewContext)
}

/** The scopes the snapshot endpoint knows; anything else reads 'all'. */
const SNAPSHOT_SCOPES = new Set([
  'all', 'Amazon US', 'Amazon CA', 'Amazon MX', 'Walmart', 'Target+', 'Temu', 'TikTok', 'eBay', 'Shopify',
])

/** A caller's marketplace → the quick view's scope. Pure; tables pass their
 *  own marketplace names ('All', 'Amazon', 'US', 'CA'…), so normalise. */
export function quickViewScope(marketplace: string | null | undefined): string {
  if (!marketplace) return 'all'
  if (marketplace === 'US') return 'Amazon US'
  if (marketplace === 'CA') return 'Amazon CA'
  if (marketplace === 'MX') return 'Amazon MX'
  return SNAPSHOT_SCOPES.has(marketplace) ? marketplace : 'all'
}
